import { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Pencil, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { api, Item } from '@/lib/api';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/lib/utils';

interface Props {
  categoryId: number;
  onEdit: (item: Item) => void;
  reloadKey: number;
  onChanged: () => void;
}

export function ItemsTable({ categoryId, onEdit, reloadKey, onChanged }: Props) {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const { push } = useToast();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .listItems(categoryId)
      .then((rows) => alive && setItems(rows))
      .catch((e) => alive && push(e instanceof Error ? e.message : '목록 실패', 'error'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [categoryId, reloadKey, push]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const ids = useMemo(() => items.map((i) => i.id), [items]);

  async function onDragEnd(ev: DragEndEvent) {
    const { active, over } = ev;
    if (!over || active.id === over.id) return;
    const from = items.findIndex((i) => i.id === active.id);
    const to = items.findIndex((i) => i.id === over.id);
    if (from < 0 || to < 0) return;
    const nextOrder = arrayMove(items, from, to);
    setItems(nextOrder);
    try {
      await api.reorderItems(
        categoryId,
        nextOrder.map((i) => i.id)
      );
      push('순서 저장됨', 'success');
      onChanged();
    } catch (e) {
      push(e instanceof Error ? e.message : '순서 저장 실패', 'error');
    }
  }

  async function onDelete(id: number) {
    if (!confirm('비활성 처리(soft delete)합니다. 계속할까요?')) return;
    try {
      await api.deleteItem(id, false);
      push('비활성 처리됨', 'success');
      setItems((prev) => prev.filter((i) => i.id !== id));
      onChanged();
    } catch (e) {
      push(e instanceof Error ? e.message : '삭제 실패', 'error');
    }
  }

  async function onToggleActive(it: Item) {
    try {
      await api.updateItem(it.id, { is_active: it.is_active ? 0 : 1 });
      setItems((prev) => prev.map((i) => (i.id === it.id ? { ...i, is_active: i.is_active ? 0 : 1 } : i)));
      onChanged();
    } catch (e) {
      push(e instanceof Error ? e.message : '토글 실패', 'error');
    }
  }

  if (loading) return <div className="p-8 text-center text-sm text-muted-foreground">로드 중…</div>;
  if (items.length === 0)
    return (
      <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
        아이템이 없습니다. "새 아이템" 버튼으로 추가하세요.
      </div>
    );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <div className="overflow-hidden rounded-md border">
        <div className="grid grid-cols-[32px_80px_1fr_1fr_80px_120px] items-center gap-3 border-b bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">
          <span></span>
          <span>썸네일</span>
          <span>제목</span>
          <span>링크</span>
          <span className="text-center">활성</span>
          <span className="text-right">동작</span>
        </div>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {items.map((it) => (
            <Row key={it.id} item={it} onEdit={onEdit} onDelete={onDelete} onToggleActive={onToggleActive} />
          ))}
        </SortableContext>
      </div>
    </DndContext>
  );
}

function Row({
  item,
  onEdit,
  onDelete,
  onToggleActive,
}: {
  item: Item;
  onEdit: (it: Item) => void;
  onDelete: (id: number) => void;
  onToggleActive: (it: Item) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'grid grid-cols-[32px_80px_1fr_1fr_80px_120px] items-center gap-3 border-b px-3 py-2 text-sm',
        isDragging && 'bg-accent/60',
        !item.is_active && 'opacity-50'
      )}
    >
      <button
        className="flex h-8 w-8 items-center justify-center text-muted-foreground hover:text-foreground"
        {...attributes}
        {...listeners}
        aria-label="drag handle"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div
        className="h-16 w-16 rounded border bg-contain bg-center bg-no-repeat"
        style={{ backgroundImage: item.thumbnail_url ? `url('${item.thumbnail_url}')` : undefined }}
      />
      <div className="min-w-0 truncate font-medium">{item.title}</div>
      <div className="min-w-0 truncate text-xs text-muted-foreground">
        {item.link_url ? (
          <a href={item.link_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline">
            <ExternalLink className="h-3 w-3" />
            {item.link_url}
          </a>
        ) : (
          '-'
        )}
      </div>
      <div className="text-center">
        <button
          onClick={() => onToggleActive(item)}
          className={cn(
            'inline-flex h-6 w-11 items-center rounded-full p-0.5 transition-colors',
            item.is_active ? 'bg-primary' : 'bg-muted'
          )}
        >
          <span
            className={cn(
              'inline-block h-5 w-5 transform rounded-full bg-background shadow transition-transform',
              item.is_active ? 'translate-x-5' : 'translate-x-0'
            )}
          />
        </button>
      </div>
      <div className="flex items-center justify-end gap-1">
        <Button variant="ghost" size="icon" onClick={() => onEdit(item)} aria-label="편집">
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={() => onDelete(item.id)} aria-label="삭제">
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>
    </div>
  );
}
